<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h1 class="h4 mb-1">Calendrier des opérations</h1>
          <p class="text-muted small mb-0">
            Ventes, achats, mouvements de stock et expirations du site connecté.
          </p>
        </div>
        <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="refreshCalendar">
          <i class="fa fa-rotate-right me-1" :class="{ 'fa-spin': isLoading }"></i>
          Actualiser
        </button>
      </div>

      <div class="calendar-legend d-flex flex-wrap gap-2 mb-3" aria-label="Légende du calendrier" data-tour="stock-calendar-legend">
        <span v-for="item in legend" :key="item.type" class="calendar-legend-item">
          <span class="calendar-legend-dot" :style="{ backgroundColor: item.color }"></span>
          {{ item.label }}
        </span>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2 mb-3">{{ errorMessage }}</div>

      <div class="card border-0 shadow-sm stock-calendar-card" data-tour="stock-calendar-grid">
        <div class="card-body p-2 p-md-3 position-relative">
          <div v-if="isLoading" class="calendar-loading" role="status">
            <i class="fa fa-spinner fa-spin me-2"></i>
            Chargement des événements...
          </div>
          <FullCalendar v-if="calendarOptions" ref="calendar" :options="calendarOptions" />
        </div>
      </div>

      <PrimevueDialog
        v-model:visible="detailDialogVisible"
        modal
        :header="selectedEvent?.title || 'Détail de l’événement'"
        :style="{ width: 'min(640px, 96vw)' }"
      >
        <div v-if="selectedEvent" class="calendar-event-detail">
          <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
            <span class="badge" :style="eventBadgeStyle(selectedEvent.type)">
              {{ eventTypeLabel(selectedEvent.type) }}
            </span>
            <span class="text-muted small">{{ formatEventDate(selectedEvent) }}</span>
          </div>

          <dl class="calendar-detail-grid mb-0">
            <template v-if="selectedEvent.type === 'sale'">
              <dt>Référence</dt><dd>{{ details.reference || '—' }}</dd>
              <dt>Client</dt><dd>{{ details.customer_name || 'Client comptoir' }}</dd>
              <dt>Caissier</dt><dd>{{ details.user_name || '—' }}</dd>
              <dt>Montant</dt><dd>{{ $format.currency(details.total_amount || 0) }}</dd>
              <dt>Articles</dt><dd>{{ details.items_count || 0 }}</dd>
            </template>

            <template v-else-if="selectedEvent.type === 'purchase'">
              <dt>Référence</dt><dd>{{ details.reference || '—' }}</dd>
              <dt>Fournisseur</dt><dd>{{ details.supplier_name || '—' }}</dd>
              <dt>Validateur</dt><dd>{{ details.user_name || '—' }}</dd>
              <dt>Montant</dt><dd>{{ $format.currency(details.total_amount || 0) }}</dd>
              <dt>Articles</dt><dd>{{ details.items_count || 0 }}</dd>
            </template>

            <template v-else-if="selectedEvent.type === 'movement'">
              <dt>Produit</dt><dd>{{ details.product_name || '—' }}</dd>
              <dt>SKU</dt><dd>{{ details.product_sku || '—' }}</dd>
              <dt>Motif</dt><dd>{{ details.reason || 'Sans motif' }}</dd>
              <dt>Opération</dt><dd>{{ details.movement_type === 'entry' ? 'Entrée' : 'Sortie' }}</dd>
              <dt>Quantité</dt><dd>{{ details.signed_quantity ?? details.quantity ?? 0 }}</dd>
              <dt>Stock</dt><dd>{{ details.stock_before ?? '—' }} → {{ details.stock_after ?? '—' }}</dd>
              <dt>Lot</dt><dd>{{ details.lot_number || '—' }}</dd>
              <dt>Auteur</dt><dd>{{ details.user_name || '—' }}</dd>
              <dt>Notes</dt><dd>{{ details.notes || '—' }}</dd>
            </template>

            <template v-else-if="selectedEvent.type === 'expiry'">
              <dt>Produit</dt><dd>{{ details.product_name || '—' }}</dd>
              <dt>SKU</dt><dd>{{ details.product_sku || '—' }}</dd>
              <dt>Lot</dt><dd>{{ details.lot_number || '—' }}</dd>
              <dt>Expiration</dt><dd>{{ $format.date(details.expiry_date) }}</dd>
              <dt>État</dt><dd>{{ expiryStatusLabel(details) }}</dd>
              <dt>Origines</dt><dd>{{ sourceLabels(details.sources) }}</dd>
              <dt>Achats</dt><dd>{{ details.purchase_references?.join(', ') || '—' }}</dd>
            </template>
          </dl>
        </div>

        <template #footer>
          <div class="d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="detailDialogVisible = false">
              <i class="fa fa-xmark me-1" aria-hidden="true"></i>
              Fermer
            </button>
            <RouterLink v-if="detailRoute" :to="detailRoute" class="btn btn-primary btn-sm">
              <i class="fa fa-eye me-1" aria-hidden="true"></i>
              Voir la fiche
            </RouterLink>
          </div>
        </template>
      </PrimevueDialog>
    </template>
  </AdminLayout>
</template>

<script>
import FullCalendar from '@fullcalendar/vue3';
import classicThemePlugin from '@fullcalendar/vue3/themes/classic';
import timeGridPlugin from '@fullcalendar/vue3/timegrid';
import listPlugin from '@fullcalendar/vue3/list';
import frLocale from '@fullcalendar/vue3/locales/fr';
import '@fullcalendar/vue3/skeleton.css';
import '@fullcalendar/vue3/themes/classic/theme.css';
import '@fullcalendar/vue3/themes/classic/palette.css';

const EVENT_STYLES = {
  sale: { color: '#198754', label: 'Ventes' },
  purchase: { color: '#0d6efd', label: 'Achats' },
  movement: { color: '#6f42c1', label: 'Mouvements' },
  expiry: { color: '#dc3545', label: 'Expirations' },
};

export default {
  name: 'StockCalendarView',
  components: { FullCalendar },
  data() {
    return {
      calendarOptions: null,
      isLoading: false,
      errorMessage: '',
      detailDialogVisible: false,
      selectedEvent: null,
      legend: Object.entries(EVENT_STYLES).map(([type, style]) => ({ type, ...style })),
    };
  },
  computed: {
    details() {
      return this.selectedEvent?.details || {};
    },
    detailRoute() {
      const event = this.selectedEvent;
      if (!event) return null;
      if (event.type === 'sale') return { name: 'SaleShow', params: { id: event.entityId } };
      if (event.type === 'purchase') return { name: 'PurchaseShow', params: { id: event.entityId } };
      const productId = event.details?.product_id || (event.type === 'expiry' ? event.entityId : null);
      return productId ? { name: 'ProductShow', params: { id: productId } } : null;
    },
  },
  created() {
    this.calendarOptions = {
      plugins: [classicThemePlugin, timeGridPlugin, listPlugin],
      themeSystem: 'classic',
      locale: frLocale,
      initialView: window.innerWidth < 768 ? 'listWeek' : 'timeGridWeek',
      headerToolbar: {
        left: 'prev,today,next',
        center: 'title',
        right: 'timeGridWeek,listWeek',
      },
      buttonText: { today: "Aujourd’hui", week: 'Semaine', list: 'Liste' },
      firstDay: 1,
      nowIndicator: true,
      allDayText: 'Journée',
      slotMinTime: '06:00:00',
      slotMaxTime: '23:00:00',
      height: 'auto',
      editable: false,
      selectable: false,
      eventStartEditable: false,
      eventDurationEditable: false,
      events: this.loadEvents,
      eventClick: this.openEventDetail,
    };
  },
  methods: {
    async loadEvents(info, successCallback, failureCallback) {
      this.isLoading = true;
      this.errorMessage = '';
      try {
        const response = await this.$api.get('/stock-calendar/events', {
          params: { start: info.startStr, end: info.endStr },
          skipErrorToast: true,
        });
        const events = (response.data?.data?.events || []).map((event) => {
          const style = EVENT_STYLES[event.type] || EVENT_STYLES.movement;
          return {
            id: event.id,
            title: event.title,
            start: event.start,
            allDay: Boolean(event.all_day),
            backgroundColor: style.color,
            borderColor: style.color,
            extendedProps: {
              type: event.type,
              entityId: event.entity_id,
              details: event.details || {},
            },
          };
        });
        successCallback(events);
      } catch (error) {
        console.error('Erreur chargement calendrier stock', error);
        this.errorMessage = 'Impossible de charger les événements de cette période.';
        failureCallback(error);
      } finally {
        this.isLoading = false;
      }
    },
    refreshCalendar() {
      this.$refs.calendar?.getApi()?.refetchEvents();
    },
    openEventDetail(info) {
      const event = info.event;
      this.selectedEvent = {
        id: event.id,
        title: event.title,
        start: event.start,
        allDay: event.allDay,
        type: event.extendedProps.type,
        entityId: event.extendedProps.entityId,
        details: event.extendedProps.details || {},
      };
      this.detailDialogVisible = true;
    },
    eventTypeLabel(type) {
      return EVENT_STYLES[type]?.label?.replace(/s$/, '') || 'Événement';
    },
    eventBadgeStyle(type) {
      const color = EVENT_STYLES[type]?.color || EVENT_STYLES.movement.color;
      return { backgroundColor: color, color: '#fff' };
    },
    formatEventDate(event) {
      if (!event?.start) return '—';
      return event.allDay ? this.$format.date(event.start) : this.$format.datetime(event.start);
    },
    expiryStatusLabel(details) {
      const days = Number(details.days_difference || 0);
      if (details.expiry_status === 'expired') return days === 0 ? "Expire aujourd’hui" : `Expiré depuis ${days} jour(s)`;
      return `Expire dans ${days} jour(s)`;
    },
    sourceLabels(sources) {
      if (!Array.isArray(sources) || !sources.length) return '—';
      return sources.map((source) => (source === 'purchase' ? 'Achat' : 'Mouvement')).join(', ');
    },
  },
};
</script>

<style scoped>
.calendar-legend-item {
  align-items: center;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.8rem;
  gap: 0.4rem;
  padding: 0.3rem 0.65rem;
}

.calendar-legend-dot {
  border-radius: 50%;
  height: 0.65rem;
  width: 0.65rem;
}

.calendar-loading {
  align-items: center;
  background: rgba(255, 255, 255, 0.78);
  display: flex;
  font-weight: 600;
  inset: 0;
  justify-content: center;
  position: absolute;
  z-index: 5;
}

.calendar-detail-grid {
  display: grid;
  gap: 0.6rem 1rem;
  grid-template-columns: minmax(8rem, 0.45fr) 1fr;
}

.calendar-detail-grid dt {
  color: #6c757d;
  font-size: 0.8rem;
  font-weight: 600;
}

.calendar-detail-grid dd {
  margin: 0;
  overflow-wrap: anywhere;
}

:deep(.fc) {
  font-size: 0.88rem;
}

:deep(.fc-event) {
  cursor: pointer;
}

@media (max-width: 767.98px) {
  :deep(.fc-header-toolbar) {
    align-items: stretch;
    flex-direction: column;
    gap: 0.6rem;
  }

  :deep(.fc-toolbar-chunk) {
    display: flex;
    justify-content: center;
  }

  .calendar-detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
